import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { trackFacebookEvent } from '../utils/facebookPixel';
import './Auth.css';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    password2: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error for this field when user types
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.password !== formData.password2) {
      newErrors.password2 = t('auth.password_mismatch') || 'Passwords do not match';
    }

    if (formData.password.length < 8) {
      newErrors.password = t('auth.password_too_short') || 'Password must be at least 8 characters';
    }

    if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth.invalid_email') || 'Invalid email address';
    }

    if (formData.phone && !/^[0-9]{11}$/.test(formData.phone)) {
      newErrors.phone = t('auth.invalid_phone') || 'Phone number must be 11 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      await register(formData);

      // Track successful registration
      trackFacebookEvent('CompleteRegistration', {
        content_name: 'New User Registration',
        status: 'success'
      });

      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      console.error('Registration error:', err);
      console.error('Error response:', err.response);
      console.error('Error data:', err.response?.data);

      const serverErrors: Record<string, string> = {};

      if (err.response?.data) {
        // Handle Django REST framework error format
        Object.keys(err.response.data).forEach(key => {
          const errorMessages = err.response.data[key];
          if (Array.isArray(errorMessages)) {
            serverErrors[key] = errorMessages[0];
          } else {
            serverErrors[key] = errorMessages;
          }
        });
      } else {
        serverErrors.general = t('auth.registration_error') || 'Registration failed. Please try again.';
      }

      console.log('Setting errors:', serverErrors);

      // Show alert for debugging
      if (Object.keys(serverErrors).length > 0) {
        const errorMsg = Object.entries(serverErrors)
          .map(([key, value]) => `${key}: ${value}`)
          .join('\n');
        alert('Registration Error:\n' + errorMsg);
      }

      setErrors(serverErrors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-wrapper">
      <div className="auth-card register-card">
        <h2 className="auth-title">{t('auth.register') || 'নিবন্ধন'}</h2>

        {errors.general && (
          <div className="auth-error">
            <i className="fas fa-exclamation-circle"></i>
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-grid">
            {/* 1. Personal Information */}
            <h3 className="form-section-title">{t('auth.personal_info') || 'Personal Profile'}</h3>
            <div className="form-group">
              <label htmlFor="first_name">{t('auth.first_name') || 'নাম'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-user-edit input-icon"></i>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  className="input-field"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder={t('auth.first_name_placeholder') || 'Ex: John'}
                />
              </div>
              {errors.first_name && <span className="field-error">{errors.first_name}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="last_name">{t('auth.last_name') || 'পদবি'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-user-edit input-icon"></i>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  className="input-field"
                  value={formData.last_name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder={t('auth.last_name_placeholder') || 'Ex: Doe'}
                />
              </div>
              {errors.last_name && <span className="field-error">{errors.last_name}</span>}
            </div>

            {/* 2. Account Access */}
            <h3 className="form-section-title">{t('auth.account_access') || 'Account Access'}</h3>
            <div className="form-group">
              <label htmlFor="username">{t('auth.username') || 'ইউজারনেম'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-id-card-alt input-icon"></i>
                <input
                  type="text"
                  id="username"
                  name="username"
                  className="input-field"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder={t('auth.username_placeholder') || 'Choose username'}
                />
              </div>
              {errors.username && <span className="field-error">{errors.username}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="email">{t('auth.email') || 'ইমেইল'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-paper-plane input-icon"></i>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="input-field"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="name@example.com"
                />
              </div>
              {errors.email && <span className="field-error">{errors.email}</span>}
            </div>

            {/* 3. Contact & Delivery */}
            <h3 className="form-section-title">{t('auth.contact_delivery') || 'Contact & Delivery'}</h3>
            <div className="form-group">
              <label htmlFor="phone">{t('auth.phone') || 'ফোন নম্বর'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-phone-alt input-icon"></i>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  className="input-field"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="01XXXXXXXXX"
                  disabled={loading}
                />
              </div>
              {errors.phone && <span className="field-error">{errors.phone}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="city">{t('auth.city') || 'শহর'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-city input-icon"></i>
                <input
                  type="text"
                  id="city"
                  name="city"
                  className="input-field"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder={t('auth.city_placeholder') || 'City'}
                />
              </div>
              {errors.city && <span className="field-error">{errors.city}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="postal_code">{t('auth.postal_code') || 'পোস্টাল কোড'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-mail-bulk input-icon"></i>
                <input
                  type="text"
                  id="postal_code"
                  name="postal_code"
                  className="input-field"
                  value={formData.postal_code}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  placeholder="1234"
                />
              </div>
              {errors.postal_code && <span className="field-error">{errors.postal_code}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="address">{t('auth.address') || 'বিস্তারিত ঠিকানা'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-map-marked-alt input-icon"></i>
                <textarea
                  id="address"
                  name="address"
                  className="input-field"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={1}
                  disabled={loading}
                  placeholder={t('auth.address_placeholder') || 'Road, Floor, House details...'}
                />
              </div>
              {errors.address && <span className="field-error">{errors.address}</span>}
            </div>

            {/* 4. Security */}
            <h3 className="form-section-title">{t('auth.security') || 'Security'}</h3>
            <div className="form-group">
              <label htmlFor="password">{t('auth.password') || 'পাসওয়ার্ড'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-user-shield input-icon"></i>
                <input
                  type="password"
                  id="password"
                  name="password"
                  className="input-field"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  disabled={loading}
                  placeholder="••••••••"
                />
              </div>
              {errors.password && <span className="field-error">{errors.password}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="password2">{t('auth.confirm_password') || 'পাসওয়ার্ড নিশ্চিত করুন'} *</label>
              <div className="input-wrapper">
                <i className="fas fa-key input-icon"></i>
                <input
                  type="password"
                  id="password2"
                  name="password2"
                  className="input-field"
                  value={formData.password2}
                  onChange={handleChange}
                  required
                  minLength={8}
                  disabled={loading}
                  placeholder="••••••••"
                />
              </div>
              {errors.password2 && <span className="field-error">{errors.password2}</span>}
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading}
          >
            {loading ? (t('auth.registering') || 'নিবন্ধন হচ্ছে...') : (t('auth.register') || 'নিবন্ধন করুন')}
          </button>
        </form>

        <div className="auth-footer">
          {t('auth.have_account') || 'ইতিমধ্যে অ্যাকাউন্ট আছে?'}
          <Link to="/login" className="auth-link">
            {t('auth.login') || 'লগইন করুন'}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;
